import {useMutation, useQuery, useQueryClient} from "react-query";
import axios from "axios";
import {PagedSearchParams} from "@atiautomacao/ati-ui-library";
import {useSnackbar} from "notistack";
import {AlarmSeverityProps} from "../../../Shared/Types/AlarmSeverity";
import {getHttpStatusMessage, HTTP_STATUS_MESSAGES} from "../../../Utils/httpStatusMessages";

const apiUrl = "api/alarmSeverity";

const saveAlarmSeverity = async (data: AlarmSeverityProps) => {
    try {
        if(data.id != null){
            return await axios.put(`${apiUrl}`, data,{timeout:30000}).then((res) => res.data);
        } else {
            return await axios.post(apiUrl, data,{timeout:30000}).then((res) => res.data);
        }
    } catch (error) {
        console.error("Error fetching data:", error);
        return Promise.reject(error);
    }
};

export const SaveAlarmSeverityReactQuery = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: (data: any) => {
            return saveAlarmSeverity(data);
        },
        onSuccess: () => {
            closeSnackbar()
            enqueueSnackbar("Severidade de alarme salva com sucesso!", { variant: "success" });
        },
        onError: (error: any) => {
            if(error?.response.status === 409 && error?.response?.data?.title?.includes("Already exists")){
                enqueueSnackbar(error?.response?.data?.detail, { variant: "error", autoHideDuration: 1500 });
                return
            }
            if(error?.response.status === 503){
                enqueueSnackbar("Serviço indisponível", { variant: "error", autoHideDuration: 1500 });
                return
            }
            const errorMessages = error?.response?.data?.detail.split('/').filter((s:any) => s.trim() !== '') ?? ["Erro desconhecido. Informe ao suporte."]
            const result = errorMessages.map((erro:any) => {
                return erro.replace(/^.*?:\s*/, "")
            })

            result.forEach((message:any) => {
                enqueueSnackbar(`${message}`, { variant: "error", autoHideDuration: 2000 });
            })

        }
    })
};

const fetchAlarmSeverity = async (pagedSearchParams: PagedSearchParams) => {
    try {
        return await axios
            .get(`${apiUrl}/search`, {
                params: pagedSearchParams ? pagedSearchParams.toURLSearchParams() : null,
                timeout: 30000
            })
            .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        return Promise.reject(error);
    }
};

export const AlarmSeverityReactQuery = (pagedSearchParams: PagedSearchParams) => {
    return useQuery({
        queryKey: ["data", pagedSearchParams],
        queryFn: () => {
            return fetchAlarmSeverity(pagedSearchParams);
        }
    });
};

const fetchAlarmSeverityById = async (id: number) => {
    try {
        return await axios.get(`${apiUrl}/${id}`,{timeout:30000}).then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        return Promise.reject(error);
    }
};

export const FindAlarmSeverityByIdReactQuery = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    return useMutation({
        mutationFn: (id: number) => {
            return fetchAlarmSeverityById(id);
        },
        onError: (error: any) => {
            closeSnackbar()
            enqueueSnackbar(`Erro ao carregar uma severidade de alarme: ${error.message}`, { variant: "error" });
        }
    })
};

const deleteAlarmSeverity = async (id: number) => {
    try {
        return await axios.delete(`${apiUrl}/${id}`,{timeout:30000}).then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        return Promise.reject(error);
    }
};

export const DeleteAlarmSeverityReactQuery = (pagedSearchParams: PagedSearchParams) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) => {
            return deleteAlarmSeverity(data.id);
        },
        onSuccess: () => {
            closeSnackbar()
            enqueueSnackbar("Severidade de alarme excluída com sucesso!", { variant: "success" });
            if (pagedSearchParams) {
                queryClient.invalidateQueries(["data", pagedSearchParams]);
            }
        },
        onError: (error: any) => {
            closeSnackbar()
            enqueueSnackbar(`Erro ao excluir severidade de alarme: ${error.message}`, { variant: "error" });
        }
    })
};

const fetchAlarmSeverities = async () => {
    try {
        // Construir a URL para o endpoint /find-all
        const url = `${apiUrl}/find-all`;

        // Fazer a requisição
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
};

export const FindAllAlarmSeverityReactQuery = () => {
    return useQuery(
        'find-all-alarm-severities', // Key for caching and invalidation
        fetchAlarmSeverities,        // Fetch function
    );
};
